import { action, flow } from 'mobx';
import { codeSplit, Controller, FetchPageOptions, StoreContainer, FetchOptions } from 'bernie-core';
import { AnalyticsStore, ContextStore } from 'bernie-plugin-mobx';
import { PageData } from 'bernie-http';
import { SystemEvent, SystemEventLevel } from 'bernie-logger';
import { getBexApiContext } from 'bernie-context';

import {
  ClientMetadataStore,
  ErrorStore,
  FlightServicingConfirmationStore,
  ServicingConfirmationCriteriaStore
} from 'stores';
import { PageId, getPageName, generateRequestOptions } from 'utility';
import { getNavigationIdFromQuery } from 'utility/navigation-identity';
import { FlightsShoppingPwaFlightServicingConfirmationResults } from 'src/__generated__/api';

export class FlightServicingConfirmationController implements Controller {
  public pageId = PageId.FLIGHT_SERVICING_CONFIRMATION;
  public path = '*Flight-Servicing-Confirmation';
  public routeName = 'flight-servicing-confirmation';
  public bundles = [];
  public exact = true;
  /* istanbul ignore next */
  public component = codeSplit(
    () => import(/* webpackChunkName: "servicing-confirmation" */ '../views/flight-servicing-confirmation')
  );
  public pendingPromises = [];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public async fetch(options: FetchOptions): Promise<any> {
    if (!options.isServer) {
      return this.fetchClientSide(options);
    }

    const { servicingConfirmationCriteriaStore } = this.getFetchVariables(options.stores);
    servicingConfirmationCriteriaStore.setCriteriaFromUrl(options);

    return Promise.resolve({});
  }

  public fetchPageData(pageData: FetchPageOptions): Promise<PageData> {
    // To-Do: Localize page title
    const title = 'Flight Servicing Confirmation';
    const query = pageData.query;
    const pageName = getPageName(this.pageId);
    const navigationId = getNavigationIdFromQuery(query);

    return Promise.resolve({ title, pageId: this.pageId, pageName, navigationId });
  }

  private fetchClientSide(options: FetchOptions) {
    if (this.pendingPromises.length) {
      this.pendingPromises.forEach(promise => promise.cancel());
    }

    const clientPromise = this.createFlowForClientFetch(options, this.pageId);

    this.pendingPromises.push(clientPromise);

    return clientPromise;
  }

  @action
  private createFlowForClientFetch = flow(function* (options: FetchOptions, pageId: string) {
    const {
      analyticsStore,
      clientMetadataStore,
      errorStore,
      flightServicingConfirmationStore,
      servicingConfirmationCriteriaStore,
      variables
    } = this.getFetchVariables(options.stores);

    const requestOptions = generateRequestOptions(options.request, options.isServer, pageId);
    servicingConfirmationCriteriaStore.setCriteriaFromUrl(options);
    errorStore.clientFetchFailure = ErrorStore.DEFAULTS.clientFetchFailure;

    try {
      const response: FlightsShoppingPwaFlightServicingConfirmationResults.FlightsServicingConfirmation = yield flightServicingConfirmationStore.fetchResults(
        variables,
        requestOptions,
        analyticsStore
      );

      if (!response || Object.keys(response).length == 0) {
        errorStore.setClientFetchFailure(true);
        this.logger.logEvent(new SystemEvent(SystemEventLevel.CRITICAL, 'SERVICING_CONFIRMATION_FETCH_FAILED'));

        return;
      }

      const { confirmationHeading, clientMetadata, errorMessaging } = response;

      clientMetadataStore.hydrate({ clientMetadata });
      errorStore.hydrate({ failedServicingMessaging: errorMessaging });

      flightServicingConfirmationStore.setConfirmationHeading(confirmationHeading);

      this.pendingPromises = [];

      return;
    } catch (error) {
      errorStore.setClientFetchFailure(true);
      this.logger.logEvent(
        new SystemEvent(SystemEventLevel.CRITICAL, 'SERVICING_CONFIRMATION_CLIENT_HYDRATION'),
        error
      );
    }
  });

  private getFetchVariables(stores: StoreContainer) {
    const analyticsStore = stores.get<AnalyticsStore>('analytics');
    const bernieContext: ContextStore = stores.get<ContextStore>('context');
    const clientMetadataStore = stores.get<ClientMetadataStore>('clientMetadataStore');
    const context = getBexApiContext(bernieContext);
    const errorStore = stores.get<ErrorStore>('errorStore');
    const flightServicingConfirmationStore = stores.get<FlightServicingConfirmationStore>(
      'flightServicingConfirmationStore'
    );
    const servicingConfirmationCriteriaStore = stores.get<ServicingConfirmationCriteriaStore>(
      'servicingConfirmationCriteriaStore'
    );
    const variables = { context, ...servicingConfirmationCriteriaStore.servicingConfirmationCriteria };

    return {
      analyticsStore,
      clientMetadataStore,
      errorStore,
      flightServicingConfirmationStore,
      servicingConfirmationCriteriaStore,
      variables
    };
  }
}
